import {
  CLARITY_PROJECT_ID,
  CLARITY_TAG_NAME,
  CLARITY_TAG_VALUE,
  IS_DEV,
} from '@/constants/constants';
import Clarity from '@microsoft/clarity';

let clarityInitialized = false;

/**
 * Microsoft Clarity utility functions for session tracking and user identification
 */

/**
 * Initialize Microsoft Clarity with project ID
 * Should be called once at application startup
 */
export const initializeClarity = (): void => {
  try {
    if (!IS_DEV && CLARITY_PROJECT_ID) {
      Clarity.init(CLARITY_PROJECT_ID);
      clarityInitialized = true;
      console.log('✅ Microsoft Clarity initialized successfully');
    } else if (IS_DEV) {
      console.log('ℹ️ Clarity disabled in development mode');
    } else {
      console.warn('⚠️ Clarity project ID not configured');
    }
  } catch (error) {
    clarityInitialized = false;
    console.error('❌ Error initializing Clarity:', error);
  }
};

export const isClarityReady = (): boolean => {
  return clarityInitialized && !IS_DEV && !!CLARITY_PROJECT_ID;
};

/**
 * Identifies a user in Microsoft Clarity for session tracking
 * @param accountId - The account ID to identify the user
 */
export const identifyClarityUser = (accountId: number | string): void => {
  // Early return if Clarity is not initialized
  if (!isClarityReady()) return;

  try {
    Clarity.identify(accountId.toString());
  } catch (error) {
    console.error('Failed to identify user in Clarity:', error);
  }
};

/**
 * Sets a page tag in Microsoft Clarity for better analytics segmentation
 * @param pageName - The page name from CLARITY_TAG_VALUE.PAGE_NAME
 */
export const setClarityPageTag = (pageName: string): void => {
  // Early return if Clarity is not initialized
  if (!isClarityReady()) return;

  try {
    Clarity.setTag(CLARITY_TAG_NAME.PAGE_NAME, pageName);
  } catch (error) {
    console.error('Failed to set Clarity page tag:', error);
  }
};

/**
 * Combined function to identify user and set page tag
 * @param accountId - The account ID to identify the user
 * @param pageName - The page name from CLARITY_TAG_VALUE.PAGE_NAME
 */
export const initializeClarityForPage = (
  accountId: number | string,
  pageName: string,
): void => {
  identifyClarityUser(accountId);
  setClarityPageTag(pageName);
};

/**
 * Set custom tags in Clarity for additional tracking
 * @param key - Tag key
 * @param value - Tag value
 */
export const setClarityCustomTag = (key: string, value: string): void => {
  // Early return if Clarity is not initialized
  if (!isClarityReady()) return;

  try {
    Clarity.setTag(key, value);
  } catch (error) {
    console.error('Failed to set custom Clarity tag:', error);
  }
};

/**
 * Available page names for Clarity tracking
 */
export const CLARITY_PAGES = CLARITY_TAG_VALUE.PAGE_NAME;
